import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, ArrowRight } from 'lucide-react';
import { importService } from '../../../services/importService';

const ImportWizard = ({ advisorId, onComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [options, setOptions] = useState({
    checkDuplicates: true,
    defaultSource: 'other',
    defaultProject: 'Residencial 3 Volcanes'
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileSelect = async (e) => {
    const selectedFile = e?.target?.files?.[0];
    if (!selectedFile) return;

    try {
      setError('');
      const { headers: csvHeaders } = await importService?.parseCSV(selectedFile);
      setFile(selectedFile);
      setHeaders(csvHeaders);
      setStep(2);
    } catch (err) {
      setError(err?.message || 'Failed to parse file');
    }
  };

  const handleFieldMapping = (field, header) => {
    setFieldMapping({ ...fieldMapping, [field]: header });
  };

  const handleImport = async () => {
    try {
      setImporting(true);
      setError('');

      const importResult = await importService?.importLeads(
        file,
        advisorId,
        fieldMapping,
        options
      );

      setResult(importResult);
      setStep(4);
      
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      setError(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const resetWizard = () => {
    setStep(1);
    setFile(null);
    setHeaders([]);
    setFieldMapping({});
    setResult(null);
    setError('');
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">Import Wizard</h2>
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className={`flex items-center gap-2 ${
            step >= 1 ? 'text-blue-600' : 'text-gray-400'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              1
            </div>
            <span className="font-medium">Upload File</span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
          <div className={`flex items-center gap-2 ${
            step >= 2 ? 'text-blue-600' : 'text-gray-400'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              2
            </div>
            <span className="font-medium">Map Fields</span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
          <div className={`flex items-center gap-2 ${
            step >= 3 ? 'text-blue-600' : 'text-gray-400'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              3
            </div>
            <span className="font-medium">Configure</span>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
          <div className={`flex items-center gap-2 ${
            step >= 4 ? 'text-blue-600' : 'text-gray-400'
          }`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              4
            </div>
            <span className="font-medium">Complete</span>
          </div>
        </div>
      </div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {/* Step 1: Upload File */}
      {step === 1 && (
        <div className="text-center py-12">
          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload CSV or Excel File</h3>
          <p className="text-gray-600 mb-6">Select a file containing your lead data</p>
          <label className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <FileText className="w-4 h-4 inline mr-2" />
            Choose File
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </div>
      )}
      {/* Step 2: Map Fields */}
      {step === 2 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Map CSV Columns to Lead Fields</h3>
          <p className="text-gray-600 mb-6">Match your CSV columns to the corresponding lead fields</p>
          
          <div className="space-y-4">
            {[
              { field: 'nombre', label: 'Name', required: true },
              { field: 'email', label: 'Email', required: true },
              { field: 'telefono', label: 'Phone', required: false },
              { field: 'fuente', label: 'Source', required: false },
              { field: 'proyectoInteres', label: 'Project Interest', required: false },
              { field: 'presupuesto', label: 'Budget', required: false },
              { field: 'nivelInteres', label: 'Interest Level', required: false },
              { field: 'notas', label: 'Notes', required: false }
            ]?.map(({ field, label, required }) => (
              <div key={field} className="flex items-center gap-4">
                <label className="w-48 font-medium text-gray-700">
                  {label}
                  {required && <span className="text-red-600 ml-1">*</span>}
                </label>
                <select
                  value={fieldMapping?.[field] || ''}
                  onChange={(e) => handleFieldMapping(field, e?.target?.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">-- Select Column --</option>
                  {headers?.map((header) => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={!fieldMapping?.nombre || !fieldMapping?.email}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {/* Step 3: Configure Options */}
      {step === 3 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Configuration</h3>
          <p className="text-gray-600 mb-6">Configure import settings and validation rules</p>

          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={options?.checkDuplicates}
                  onChange={(e) => setOptions({ ...options, checkDuplicates: e?.target?.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="font-medium text-gray-700">Check for duplicate leads</span>
              </label>
              <p className="text-sm text-gray-500 ml-6">Skip leads with matching email or phone</p>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-2">Default Source</label>
              <select
                value={options?.defaultSource}
                onChange={(e) => setOptions({ ...options, defaultSource: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="referral">Referral</option>
                <option value="website">Website</option>
                <option value="social_media">Social Media</option>
                <option value="direct">Direct</option>
                <option value="event">Event</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block font-medium text-gray-700 mb-2">Default Project</label>
              <input
                type="text"
                value={options?.defaultProject}
                onChange={(e) => setOptions({ ...options, defaultProject: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Residencial 3 Volcanes"
              />
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={() => setStep(2)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleImport}
              disabled={importing}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {importing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Start Import
                </>
              )}
            </button>
          </div>
        </div>
      )}
      {/* Step 4: Complete */}
      {step === 4 && result && (
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Complete!</h3>
          
          <div className="max-w-md mx-auto mt-6 space-y-3">
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-gray-700">Successfully Imported</span>
              <span className="font-bold text-green-600">{result?.successful}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
              <span className="text-gray-700">Duplicates Found</span>
              <span className="font-bold text-yellow-600">{result?.duplicates}</span>
            </div>
            <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
              <span className="text-gray-700">Failed</span>
              <span className="font-bold text-red-600">{result?.failed}</span>
            </div>
          </div>

          {result?.errors?.length > 0 && (
            <div className="mt-6 max-w-2xl mx-auto">
              <h4 className="font-semibold text-gray-900 mb-3">Error Details</h4>
              <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-4 text-left">
                {result?.errors?.map((err, index) => (
                  <div key={index} className="mb-2 text-sm">
                    <span className="font-medium text-gray-700">Row {err?.row}:</span>
                    <span className="text-red-600 ml-2">{err?.errors?.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={resetWizard}
            className="mt-8 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Import Another File
          </button>
        </div>
      )}
    </div>
  );
};

export default ImportWizard;